import api from './api';

// Helper: choose storage based on "remember me" preference
function getStorage() { // eslint-disable-line no-unused-vars
  // If token is in localStorage, user chose "remember me"
  // If in sessionStorage, they didn't. Default to sessionStorage for new sessions.
  if (localStorage.getItem('token')) return localStorage;
  if (sessionStorage.getItem('token')) return sessionStorage;
  // For new login, check the rememberMe flag set before calling login
  return authService._rememberMe ? localStorage : sessionStorage;
}

function saveAuth(token, user) {
  const storage = authService._rememberMe ? localStorage : sessionStorage;
  // Clear from both storages first to avoid duplicates
  localStorage.removeItem('token');
  localStorage.removeItem('user');
  sessionStorage.removeItem('token');
  sessionStorage.removeItem('user');
  // Save to the chosen storage
  storage.setItem('token', token);
  storage.setItem('user', JSON.stringify(user));
}

function clearAuth() {
  localStorage.removeItem('token');
  localStorage.removeItem('user');
  sessionStorage.removeItem('token');
  sessionStorage.removeItem('user');
}

// Exported helper for api.js interceptor
export function getToken() {
  return localStorage.getItem('token') || sessionStorage.getItem('token');
}

const authService = {
  _rememberMe: true, // default: remember

  setRememberMe(value) {
    this._rememberMe = value;
  },

  register: async (userData) => {
    const response = await api.post('/auth/register', userData);
    if (response.data.token) {
      saveAuth(response.data.token, response.data.user);
    }
    return response.data;
  },

  login: async (email, password, recaptchaToken) => {
    const deviceToken = localStorage.getItem(`trusted_device_${email}`);
    const response = await api.post('/auth/login', { email, password, recaptchaToken, deviceToken });
    if (response.data.token) {
      saveAuth(response.data.token, response.data.user);
    }
    return response.data;
  },

  send2FACode: async (twoFactorSession, method, recaptchaToken = null) => {
    const response = await api.post('/auth/2fa/send', { twoFactorSession, method, recaptchaToken });
    return response.data;
  },

  verify2FACode: async (twoFactorSession, code, rememberDevice = false) => {
    const response = await api.post('/auth/2fa/verify', { twoFactorSession, code, rememberDevice });
    if (response.data.token) {
      saveAuth(response.data.token, response.data.user);
      if (response.data.deviceToken) {
        const email = response.data.user?.email;
        if (email) localStorage.setItem(`trusted_device_${email}`, response.data.deviceToken);
      }
    }
    return response.data;
  },

  verifyEmail: async (verificationToken, code) => {
    const response = await api.post('/auth/verify-email', { verificationToken, code });
    if (response.data.token) {
      saveAuth(response.data.token, response.data.user);
    }
    return response.data;
  },

  resendVerificationEmail: async (verificationToken) => {
    const response = await api.post('/auth/resend-verification', { verificationToken });
    return response.data;
  },

  googleLogin: async ({ credential, access_token } = {}) => {
    const response = await api.post('/auth/google', { credential, access_token });
    if (response.data.token) {
      saveAuth(response.data.token, response.data.user);
    }
    return response.data;
  },

  logout: () => {
    clearAuth();
  },

  getCurrentUser: () => {
    const userStr = localStorage.getItem('user') || sessionStorage.getItem('user');
    return userStr ? JSON.parse(userStr) : null;
  },

  getProfile: async () => {
    const response = await api.get('/auth/me');
    return response.data;
  },

  updateProfile: async (data) => {
    const response = await api.put('/auth/update-profile', data);
    return response.data;
  },

  changePassword: async (currentPassword, newPassword) => {
    const response = await api.put('/auth/change-password', {
      current_password: currentPassword,
      new_password: newPassword,
    });
    return response.data;
  },

  verifyResetToken: async (token) => {
    const response = await api.get(`/auth/verify-reset-token/${token}`);
    return response.data;
  },

  forgotPassword: async (email) => {
    const response = await api.post('/auth/forgot-password', { email });
    return response.data;
  },

  resetPassword: async (token, password) => {
    const response = await api.post('/auth/reset-password', { token, password });
    return response.data;
  },
};

export default authService;
